import {match, Pattern} from 'ts-pattern';

import {useEffect} from 'react';

import {recordAiAssistantError} from '@dms/ai-assistant';

const getRejectionMessage = (reason: unknown): string =>
  match(reason)
    .with(Pattern.instanceOf(Error), (error) => error.message)
    .with(Pattern.string, (text) => text)
    .otherwise(() => 'Unhandled promise rejection');

/**
 * Records uncaught browser errors into the store. A named `useEffect` with cleanup is the sanctioned
 * raw-effect case (a `window` subscription); `recordAiAssistantError` writes with no re-render.
 */
export function AiAssistantErrorSync(): null {
  useEffect(function subscribeToBrowserErrors() {
    const handleError = (event: ErrorEvent) => {
      recordAiAssistantError({source: 'browser', message: event.message});
    };
    const handleRejection = (event: PromiseRejectionEvent) => {
      recordAiAssistantError({source: 'browser', message: getRejectionMessage(event.reason)});
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  return null;
}
